/**
 * logger.js — TabedLab
 *
 * Tiny env-aware console wrapper. In production builds (NODE_ENV === 'production'),
 * only errors and warnings are forwarded — info/debug logs are no-ops. This:
 *   • Removes noisy debug logs from production browser consoles.
 *   • Prevents accidental leakage of sensitive payloads in console.log.
 *   • Keeps real errors visible so PostHog session recordings still capture them.
 *
 * Usage (drop-in replacement for console):
 *   import { logger } from '@/lib/logger';
 *   logger.error('Realtime fetch error:', err);
 *   logger.info('User signed in');     // suppressed in production
 *   logger.debug('Payload:', payload); // suppressed in production
 */

const isProduction =
  typeof process !== 'undefined' &&
  process.env &&
  process.env.NODE_ENV === 'production';

const noop = () => {};

export const logger = {
  // Always forwarded — real failures must remain visible
  error: (...args) => {
    if (typeof console !== 'undefined' && console.error) console.error(...args);
  },
  warn: (...args) => {
    if (typeof console !== 'undefined' && console.warn) console.warn(...args);
  },
  // Suppressed in production
  info: isProduction ? noop : (...args) => {
    if (typeof console !== 'undefined' && console.info) console.info(...args);
  },
  log: isProduction ? noop : (...args) => {
    if (typeof console !== 'undefined' && console.log) console.log(...args);
  },
  debug: isProduction ? noop : (...args) => {
    if (typeof console !== 'undefined' && console.debug) console.debug(...args);
  },
};

export default logger;
